import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "@tanstack/react-router";
import { Target } from "lucide-react";
import {
  type CohortReport,
  createCohort,
  listCohorts,
  previewCohort,
} from "@/api/close-cohorts";
import { Page } from "@/components/page";
import { EmptyState } from "@/components/empty-state";

/**
 * Phase A of the targeted-rescan flow: turn a Close search query into a
 * frozen cohort plus a run source.
 *
 * Preview is deliberately a separate button. Materializing writes one row
 * per lead and a run source; the operator should see the count first, since
 * a malformed query is far more likely than a genuinely empty result
 * (`-lead_status:"X"` silently returns nothing instead of erroring).
 */
export function CohortsPage() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [query, setQuery] = useState("");
  const [preview, setPreview] = useState<number | null>(null);
  const [report, setReport] = useState<CohortReport | null>(null);

  const cohorts = useQuery({ queryKey: ["cohorts"], queryFn: listCohorts });

  const doPreview = useMutation({
    mutationFn: () => previewCohort(query),
    onSuccess: (r) => {
      setPreview(r.total);
      toast.success(`${r.total} lead${r.total === 1 ? "" : "s"} match`);
    },
    onError: () => setPreview(null),
  });

  const doCreate = useMutation({
    mutationFn: () => createCohort(name.trim(), query),
    onSuccess: (r) => {
      setReport(r);
      toast.success(`Cohort materialized: ${r.lead_count} leads`);
      qc.invalidateQueries({ queryKey: ["cohorts"] });
    },
  });

  const items = cohorts.data ?? [];

  return (
    <Page title="Close cohorts">
      <section className="card p-4">
        <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Target className="h-4 w-4" /> New cohort from a Close query
        </h2>
        <div className="grid gap-3">
          <div>
            <label className="label">Name</label>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="stale-shopware-2025"
            />
          </div>
          <div>
            <label className="label">Close search query</label>
            <textarea
              className="input min-h-[6rem] font-mono text-xs"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPreview(null);
              }}
              placeholder={'opportunity_status:"Unqualifiziert" and date_created < "2026-01-01"'}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Use <code>not(lead_status:"X")</code> to exclude — the{" "}
              <code>-lead_status:"X"</code> form silently returns zero rows.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn"
              disabled={!query.trim() || doPreview.isPending}
              onClick={() => doPreview.mutate()}
            >
              {doPreview.isPending ? "Counting…" : "Preview"}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={
                preview === null || !name.trim() || doCreate.isPending
              }
              onClick={() => doCreate.mutate()}
            >
              {doCreate.isPending ? "Materializing…" : "Materialize"}
            </button>
            {preview !== null && (
              <span className="text-sm text-muted-foreground">
                {preview} lead{preview === 1 ? "" : "s"} match
              </span>
            )}
          </div>
        </div>

        {report && (
          <div className="mt-4 rounded border p-3 text-sm">
            <div className="font-semibold mb-2">Materialization report</div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
              <Stat label="Leads" value={report.lead_count} />
              <Stat label="Distinct URLs" value={report.url_count} />
              <Stat label="No URL" value={report.no_url} />
              <Stat label="From contact" value={report.from_contact} />
              <Stat label="Unnormalizable" value={report.unnormalizable} />
              <Stat label="Shared-URL groups" value={report.shared_url_groups} />
              <Stat label="Leads on shared URLs" value={report.shared_url_leads} />
            </dl>
            <p className="text-xs text-muted-foreground mt-2">
              Leads on shared URLs are all updated — the map knows every lead
              per URL. The lead/URL delta is expected, not a loss.
            </p>
          </div>
        )}
      </section>

      <section className="card p-4 mt-4">
        <h2 className="text-sm font-semibold mb-3">Cohorts</h2>
        {items.length === 0 ? (
          <EmptyState title="No cohorts yet" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-1">Name</th>
                <th>Leads</th>
                <th>Scanned by</th>
                <th>Reconcile</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t">
                  <td className="py-1">
                    <Link to="/cohorts/$cohortId" params={{ cohortId: c.id }}>
                      {c.name}
                    </Link>
                  </td>
                  <td>{c.lead_count}</td>
                  <td>
                    {c.phase_b_run_id ? (
                      <Link to="/runs/$runId" params={{ runId: c.phase_b_run_id }}>
                        run
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">not scanned</span>
                    )}
                  </td>
                  <td>{c.reconcile_status ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </Page>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular-nums font-medium">{value}</dd>
    </div>
  );
}
